// Copyright (c) Microsoft. All rights reserved.

import { Constants } from '../../Constants';
import { useAppDispatch } from '../../redux/app/hooks';
import { store } from '../../redux/app/store';
import { showAlertToast } from '../../redux/features/app/appSlice';
import { IChatRoom, ChatRoomType, ChatRoom } from '../models/conversations/ChatRoom';
import { Conversations } from '../models/conversations/ConversationsState_v2';
import {
  addConversation,
  addMessageToConversationFromUser,
  leaveConversation,
  addChatMessages,
  setConversations,
  setCountinuationToken,
  setSelectedConversation,
  setChatMessages,
  deleteConversation,
  addPluginMessage,
} from '../../redux/features/conversations/conversationsSlice_v2';
import { AlertType } from '../models/AlertType';
// import { ChatArchive } from '../models/ChatArchive';
import {
  ChatMessage,
  ChatMessageList,
  ChatMessageType,
  ChatRole,
  TextContent,
} from '../models/conversations/ChatMessage';
// import { IChatSession, ICreateChatSessionResponse } from '../models/ChatSession';
import { IAskVariables } from '../semantic-kernel/model/Ask';
import { AIModelAPI, AIModels } from '../models/app/AIModel';
import { PluginAPI, WebSearchSource } from '../models/app/Plugin';
import { IChatKeyword } from '../models/conversations/ChatKeyword';
import { IFileAnalyzedCacheResult } from '../models/conversations/File';
import { IChatUser } from '../models/users/ChatUser';

import { getErrorDetails } from '../../components/utils/TextUtils';
import { EOpenScopeType, IPluginPromptData } from '../models/studio/PluginPromptList';

import { addModels, setSelectedModel } from '../../redux/features/app/aiModelsSlice';
import { setSelectedPlugin, resetPluginInput, addPlugin } from '../../redux/features/app/pluginsSlice';
import { useNavigate } from 'react-router-dom';
import { IMCPTool } from '../models/app/mcp/Tool';
import { ChatService_v2 } from '../services/ChatService_v2';
import { PluginService } from '../services/PluginService';

// 에이전트 복원 요청 순번 (늦은 응답 무시용)
let restoreAgentSeq = 0;

// 에이전트 사용 가능 여부 (공개 / 등록자 / 기여자 / 공유받은 사람)
const canUseAgent = (agent: IPluginPromptData, userId: string): boolean =>
  !!userId &&
  !agent.deleted &&
  (agent.openScope === EOpenScopeType.Public ||
    agent.createdMember?.userId === userId ||
    !!agent.managedMembers?.some((m) => m.user?.userId === userId) ||
    (agent.openScope === EOpenScopeType.Part && !!agent.selectedMembers?.some((m) => m.userId === userId)));

export interface GetResponseOptions {
  messageType: ChatMessageType;
  value: string;
  chatId: string;
  sessionId: string;
  model: string;
  plugin?: string;
  agentId?: string;
  kernelArguments?: IAskVariables[];
  processPlan?: boolean;
  attachFiles?: IFileAnalyzedCacheResult[];
  cacheMode?: boolean;
  isDraft?: boolean;
  webSearchOn?: boolean;
  webSearchSource?: WebSearchSource;
  mcpTools?: IMCPTool[];
  agentMode?: boolean;
  additionalProperties?: Record<string, object>;
}

export const useChat = () => {
  const dispatch = useAppDispatch();
  // const { selectedId, conversations, continuationToken } = useAppSelector((state: RootState) => state.conversations);
  // const { selectedPlugin } = useAppSelector((state: RootState) => state.plugins);
  // const { user } = useAppSelector((state: RootState) => state.users);

  const chatService = new ChatService_v2();
  const pluginService = new PluginService();

  const routing = useNavigate();

  const getChatUserById = (id: string, chatId: string, users: IChatUser[]) => {
    if (id === `${chatId}-bot` || id.toLocaleLowerCase() === 'bot') return Constants.bot.profile;
    return users.find((user) => user.id === id);
  };

  const createChat = async () => {
    // const chatTitle = `Copilot @ ${new Date().toLocaleString()}`;
    try {
      const selectedId = store.getState().conversations_v2.selectedId;
      const newChatId = await chatService.createChatAsync().then((result: IChatRoom) => {
        const newChat = {
          id: result.id,
          title: result.title,
          type: result.type,
          userId: result.userId,
          createdTime: result.createdTime,
          updatedTime: result.updatedTime,
          botResponseStatus: result.botResponseStatus,
          isTyping: result.isTyping,
          partitionKey: result.partitionKey,
          messages: {
            data: [],
            continuationToken: null,
          } as ChatMessageList
        }

        if (selectedId !== '') dispatch(leaveConversation());
        dispatch(addConversation(newChat));

        return newChat.id;
      });
      return newChatId;
    } catch (e: any) {
      console.log(e);
      const errorMessage = `채팅방 생성 중에 문제가 발생했어요. 현상이 지속되면 관리자에게 문의해주세요.`;
      dispatch(showAlertToast({ altMessage: errorMessage, altType: AlertType.Info }));
    }
    return '';
  };
  const createTestChat = (id: string, userId: string) => {
    try {
      const selectedId = store.getState().conversations.selectedId;
      const newChat: ChatRoom = {
        id: id,
        title: '',
        type: ChatRoomType.TestRoom,
        userId: userId,
        createdTime: new Date(),
        updatedTime: new Date(),
        messages: {
          data: [],
          continuationToken: null,
        } as ChatMessageList, // 메시지가 없는 초기 상태
        botResponseStatus: false,
        isTyping: false,
        partitionKey: userId,
      };

      if (selectedId !== '') dispatch(leaveConversation());
      dispatch(addConversation(newChat));

      return newChat.id;
    } catch (e: any) {
      console.log(e);
      const errorMessage = `채팅방 생성 중에 문제가 발생했어요. 현상이 지속되면 관리자에게 문의해주세요.`;
      dispatch(showAlertToast({ altMessage: errorMessage, altType: AlertType.Info }));
    }
    return '';
  };
  const getResponse = ({
    messageType,
    value,
    chatId,
    sessionId,
    model,
    plugin,
    agentId,
    attachFiles,
    kernelArguments,
    cacheMode,
    isDraft,
    webSearchOn,
    webSearchSource,
    mcpTools,
    agentMode,
    additionalProperties,
  }: GetResponseOptions) => {
    dispatch(
      addMessageToConversationFromUser({
        message: value,
        chatId: chatId,
        sessionId: sessionId,
        model,
        agentId,
        plugin,
        attachFiles,
        cacheMode,
        isDraft,
        webSearchOn,
        webSearchSource,
        mcpTools,
        agentMode,
        additionalProperties,
      }),
    );

    const ask = {
      input: value,
      variables: [
        {
          key: 'chatId',
          value: chatId,
        },
        {
          key: 'messageType',
          value: messageType.toString(),
        },
      ],
    };

    if (kernelArguments) {
      ask.variables.push(...kernelArguments);
    }

    // 봇 응답 요청 사용 안함
    // try {
    // await chatService
    //     .getBotResponseAsync(
    //         ask,
    //         await AuthHelper.getSKaaSAccessToken(instance, inProgress),
    //     )
    //     .catch((e: any) => {
    //         throw e;
    //     });

    // } catch (e: any) {
    //     dispatch(updateBotResponseStatus({ chatId, status: undefined }));

    //     const errorDetails = getErrorDetails(e);
    //     if (errorDetails.includes('Failed to process plan')) {
    //         // Error should already be reflected in bot response message. Skip alert.
    //         return;
    //     }

    //     const action = processPlan ? 'execute plan' : 'generate bot response';
    //     const errorMessage = `Unable to ${action}. Details: ${getErrorDetails(e)}`;
    //     dispatch(showAlertToast({ message: errorMessage, type: AlertType.Error }));
    // }
  };

  const loadChats = async (chatRoomId?: string) => {
    try {
      // 채팅 조회 api 없음
      // const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
      const continuationToken = store.getState().conversations_v2.continuationToken;
      const response = chatRoomId
        ? await chatService.getAllChatsByChatRoomIdAsync(chatRoomId)
        : await chatService.getAllChatsAsync(continuationToken);
      const chatRooms = response.data;
      const status = await chatService.getChatStatus();
      if (chatRooms.length > 0) {
        const loadedConversations: Conversations = {};
        for (const chatRoom of chatRooms) {
          loadedConversations[chatRoom.id] = {
            id: chatRoom.id,
            title: chatRoom.title,
            type: ChatRoomType.ChatRoom,
            userId: chatRoom.userId,
            createdTime: chatRoom.createdTime,
            updatedTime: chatRoom.updatedTime,
            messages: {
              data: [],
              continuationToken: null,
            },
            isTyping: false,
            botResponseStatus: status.find(s => s.chatRoomId == chatRoom.id)?.isActive ?? false,
            partitionKey: chatRoom.partitionKey,
          };
        }

        dispatch(setConversations(loadedConversations));
        dispatch(setCountinuationToken(response.continuationToken));
      }

      // const response = await chatService.getAllChatsAsync(continuationToken);
      // const chatRooms = response.data;
      // if (chatRooms.length > 0) {
      //     const loadedConversations: Conversations = {};
      //     for (const chatRoom of chatRooms) {
      //         loadedConversations[chatRoom.id] = {
      //             id: chatRoom.id,
      //             title: chatRoom.title,
      //             type: ChatRoomType.ChatRoom,
      //             userId: chatRoom.userId,
      //             createdTime: chatRoom.createdTime,
      //             updatedTime: chatRoom.updatedTime,
      //             messages: {
      //                 data: [],
      //                 continuationToken: null,
      //             },
      //             isTyping: false,
      //             botResponseStatus: false,
      //             isVisited: false,
      //             aiModel: chatRoom.aiModel ? chatRoom.aiModel : AIModels[0]
      //         };
      //     }
      //     // const updatedConversations =
      //     //     continuationToken !== null ? { ...conversations, ...loadedConversations } : loadedConversations;
      //     // dispatch(setConversations(updatedConversations));
      //     dispatch(setConversations(loadedConversations));
      //     dispatch(setCountinuationToken(response.continuationToken));

      //     // if (isToday(new Date(loadedConversations[chatRooms[0].id].createdTime)) && isEmpty(conversations)) {
      //     //     await changeChatRoom(chatRooms[0].id);
      //     // }
      // } else {
      //     // No chats exist, create first chat window
      //     // await createChat();
      // }
      // 채팅 조회 api 없음 end
      return true;
    } catch (e: any) {
      const errorMessage = `채팅방을 불러오는 중에 문제가 발생했어요. 상단 새로고침 버튼을 눌러주세요.`;
      dispatch(showAlertToast({ altMessage: errorMessage, altType: AlertType.Info }));

      return false;
    }
  };

  const createChatRoom = (fromPlugin: boolean) => {
    const selectedPlugin = store.getState().plugins.selectedPlugin;
    createChat()
      .then((r) => {
        if (fromPlugin) {
          if (selectedPlugin) {
            addPluginStartMessage(r, selectedPlugin, store.getState().users.user.userId, selectedPlugin.aiOnUIConnectData?.openingStatement);
          }
        } else {
          dispatch(setSelectedPlugin(null));
          dispatch(resetPluginInput(undefined));
        }
        routing(`/chat/${r}`);
      })
      .catch(() => {
        const message = `채팅방 생성 중에 문제가 발생했어요. 현상이 지속되면 관리자에게 문의해주세요.`;
        dispatch(
          showAlertToast({
            altMessage: message,
            altType: AlertType.Info,
          }),
        );
      });
  };
  const changeChatRoom = async (id: string) => {
    const selectedId = store.getState().conversations_v2.selectedId;
    if (selectedId !== '') dispatch(leaveConversation());
    if (id !== '' && store.getState().conversations_v2.conversations[id].messages.data.length == 0) {
      await loadMessage(id, true)
        .then(() => {
          dispatch(setSelectedConversation({id: id}));
        })
        .catch((e: any) => {
          console.log(e);
          const errorMessage = `채팅방 메시지를 불러오는 중에 문제가 발생했어요. 상단 새로고침 버튼을 눌러주세요.`;
          dispatch(showAlertToast({ altMessage: errorMessage, altType: AlertType.Info }));

          return false;
        });
    } else {
      dispatch(setSelectedConversation({id: id}));
      if (id !== '') {
        const messages = store.getState().conversations_v2.conversations[id].messages.data;
        restoreSelectedAgent(messages[messages.length - 1]?.requestMeta?.agentId);
      }
    }
  };

  // 채팅방 이동 시 마지막 대화에 사용된 에이전트를 다시 선택한다.
  const restoreSelectedAgent = (agentId?: string) => {
    const isSelectable = !!agentId && !agentId.startsWith('Agent:') && !agentId.startsWith('Plugin:');
    const normalizedId = isSelectable ? agentId.replace(/-draft$/, '') : undefined;

    const seq = ++restoreAgentSeq;

    if (!normalizedId) {
      dispatch(setSelectedPlugin(null));
      return;
    }

    // 목록에 있으면 바로 선택
    if (store.getState().plugins.plugins[normalizedId]) {
      dispatch(setSelectedPlugin(normalizedId));
      return;
    }

    // 목록에 없으면 단건 조회 후 사용 가능할 때만 선택
    dispatch(setSelectedPlugin(null));
    pluginService
      .getAgentAsync(normalizedId)
      .then((detail) => {
        // 다른 방으로 이동했으면 무시
        if (seq !== restoreAgentSeq || detail.hidden) return;
        if (!canUseAgent(detail, store.getState().users.user.userId)) return;

        const agent: PluginAPI = {
          id: normalizedId,
          name: detail.name ?? '',
          model: detail.model ?? null,
          persona: detail.customPersona ?? '',
          description: detail.description ?? '',
          profileUrl: detail.profileUrl ?? '',
          externalSource: detail.externalSource,
          aiOnUIConnectData: detail.aiOnUIConnectData,
          conversationStarters: detail.conversationStarters,
          tags: detail.tags,
          hidden: detail.hidden,
        };
        dispatch(addPlugin(agent));
        dispatch(setSelectedPlugin(normalizedId));
      })
      .catch(() => {});
  };

    const loadMessage = (id: string, start: boolean): Promise<boolean> => {
    const continuationToken: string | null = start ? null : store.getState().conversations_v2.conversations[id].messages.continuationToken;
    const result = chatService.getChatMessagesAsync(id, continuationToken).then((r) => {
      if (r.data.length > 0) {
        if (start) {
          dispatch(setChatMessages({ id: id, messages: r }));
          const message = r.data[r.data.length - 1];
          if(message.requestMeta?.model) dispatch(setSelectedModel(message.requestMeta.model));
          restoreSelectedAgent(message.requestMeta?.agentId);
        } else {
          dispatch(addChatMessages({ id: id, messages: r }));
        }
        if (r.continuationToken !== null) return true;
        else return false;
      } else return false;
    });
    return result;
  };

  const addPluginStartMessage = (selectedId: string, plugin: PluginAPI, userId: string, startMessage?: string) => {
    const MakePluginMessage = (selectedId: string, plugin: PluginAPI) => {
      const defaultMessage = `안녕하세요. ${plugin.name} 입니다. \n 다음으로 이런 일들을 도와드릴 수 있어요.`;
      const openingMessage = startMessage?.trim() ? startMessage : defaultMessage;
      const chatMessage: ChatMessage = {
        id: selectedId + String(Math.random()),
        sessionId: selectedId,
        messageId: selectedId + String(Math.random()),
        requestMeta:{},
        author:{
          userId: plugin.id,
          userName: plugin.name,
          profileUrl: plugin.profileUrl
        },
        contents: [{$type: 'text', text: openingMessage,
                    additionalProperties:{conversationStarters: plugin.conversationStarters as object}} as TextContent] ,
        role: ChatRole.System,
        type: ChatMessageType.ChatMessage,
        partitionKey: userId

      }
      chatService.createChatMessage(chatMessage).catch(() => {
        console.log('createChatMessage 생성 오류');
      });
      dispatch(addPluginMessage({ message: chatMessage }));
    };

    if (selectedId === '') {
      createChat()
        .then((r) => {
          MakePluginMessage(r, plugin);
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      MakePluginMessage(selectedId, plugin);
    }
  };
  // const uploadBot = async (bot: ChatArchive) => {
  //     try {
  //         const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
  //         await botService.uploadAsync(bot, accessToken).then(async (chatSession: IChatSession) => {
  //             const chatMessages = await chatService.getChatMessagesAsync(chatSession.id, 0, 100, accessToken);

  //             const newChat: ChatState = {
  //                 id: chatSession.id,
  //                 title: chatSession.title,
  //                 systemDescription: chatSession.systemDescription,
  //                 memoryBalance: chatSession.memoryBalance,
  //                 users: [loggedInUser],
  //                 messages: chatMessages,
  //                 enabledHostedPlugins: chatSession.enabledPlugins,
  //                 botProfilePicture: getBotProfilePicture(Object.keys(conversations).length),
  //                 input: '',
  //                 botResponseStatus: undefined,
  //                 userDataLoaded: false,
  //                 disabled: false,
  //                 hidden: false,
  //             };

  //             dispatch(addConversation(newChat));
  //         });
  //     } catch (e: any) {
  //         const errorMessage = `Unable to upload the bot. Details: ${getErrorDetails(e)}`;
  //         dispatch(showAlertToast({ message: errorMessage, type: AlertType.Error }));
  //     }
  // };

  // const getBotProfilePicture = (index: number): string => {
  //     return botProfilePictures[index % botProfilePictures.length];
  // };

  const getSemanticMemories = async (chatId: string, memoryName: string) => {
    try {
      return await chatService.getSemanticMemoriesAsync(chatId, memoryName);
    } catch (e: any) {
      const errorMessage = `웬디를 이용할 수 없어요. Unable to get semantic memories. Details: ${getErrorDetails(e)}`;
      dispatch(showAlertToast({ altMessage: errorMessage, altType: AlertType.Info }));
    }

    return [];
  };

  // const joinChat = async (chatId: string) => {
  //     try {
  //         const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
  //         await chatService.joinChatAsync(chatId, accessToken).then(async (result: IChatSession) => {
  //             // Get chat messages
  //             const chatMessages = await chatService.getChatMessagesAsync(result.id, 0, 100, accessToken);

  //             // Get chat users
  //             const chatUsers = await chatService.getAllChatParticipantsAsync(result.id, accessToken);

  //             const newChat: ChatRoom = {
  //                 id: string;
  //                 title: string;
  //                 type: ChatRoomType;
  //                 userId: loggedInUser;
  //                 createTime: Date;
  //                 updateTime: Date;
  //             };

  //             dispatch(addConversation(newChat));
  //         });
  //     } catch (e: any) {
  //         const errorMessage = `Error joining chat ${chatId}. Details: ${getErrorDetails(e)}`;
  //         return { success: false, message: errorMessage };
  //     }

  //     return { success: true, message: '' };
  // };

  const editChat = async (chatRoomId: string, title: string) => {
    try {
      const chatRoom: ChatRoom = store.getState().conversations_v2.conversations[chatRoomId];
      const newchatRoom: ChatRoom = {
        id: chatRoom.id,
        title: title,
        type: ChatRoomType.ChatRoom,
        userId: chatRoom.userId,
        createdTime: chatRoom.createdTime,
        updatedTime: new Date(),
        messages: chatRoom.messages,
        botResponseStatus: chatRoom.botResponseStatus,
        isTyping: chatRoom.isTyping,
        partitionKey: chatRoom.partitionKey,
      };
      await chatService.editChatAsync(newchatRoom);
    } catch (e: any) {
      const errorMessage = `채팅방 이름을 수정하는 중에 문제가 발생했어요. 다시 시도해주세요.`;
      dispatch(showAlertToast({ altMessage: errorMessage, altType: AlertType.Info }));
    }
  };

  const getServiceInfo = async () => {
    try {
      return await chatService.getServiceInfoAsync();
    } catch (e: any) {
      const errorMessage = `서비스 정보를 불러오는 중에 문제가 발생했어요.  Details: ${getErrorDetails(e)}`;
      dispatch(showAlertToast({ altMessage: errorMessage, altType: AlertType.Info }));

      return undefined;
    }
  };

  const deleteChat = async (chatRoom: ChatRoom) => {
    await chatService
      .deleteChatAsync(chatRoom)
      .then(async () => {
        //selectedId = '';
        await changeChatRoom('');
        routing(`/`);
        dispatch(deleteConversation(chatRoom));
      })
      .catch((e: any) => {
        console.log(e);
        dispatch(
          showAlertToast({
            altMessage: `채팅방 삭제를 하지 못했어요. 다시 시도해주세요.`,
            altType: AlertType.Info,
            // onRetry: () => void deleteChat(chatRoom),
          }),
        );
      });
  };

  const getAllModel = async (): Promise<boolean> => {
    const aiModels: AIModels = {};
    const result = await chatService.getAllModelAsync().then((data) => {
      if (data.length > 0) {
        data.map((model: AIModelAPI) => {
          aiModels[model.model] = {
            model: model.model,
            displayName: model.displayName,
            description: model.description,
            isAvailable: model.isAvailable,
            vendor: model.vendor,
            usage: model.usage,
            profileUrl: model.profileUrl,
            isDefault: model.isDefault
          };
        });

        dispatch(addModels(aiModels));

        return true;
      } else {
        return false;
      }
    });

    return result;
  };

  const getChatKeywords = async (): Promise<IChatKeyword[]> => {
    const result = await chatService.getChatKeywords();
    return result;
  };

  // const isEmpty = (obj: Conversations) => {
  //     return Object.keys(obj).length === 0;
  // };



  const uploadFile = async (
    file: File,
    agentId?: string,
    sessionId?: string,
  ): Promise<IFileAnalyzedCacheResult> => {
    const result = await chatService.getFileAnaylzedCacheResult(file, agentId, sessionId);
    return result;
  };


  return {
    getChatUserById,
    createChat,
    createTestChat,
    loadChats,
    getResponse,
    // uploadBot,
    getSemanticMemories,
    // joinChat,
    editChat,
    getServiceInfo,
    deleteChat,
    createChatRoom,
    changeChatRoom,
    loadMessage,
    addPluginStartMessage,
    getAllModel,
    getChatKeywords,
    uploadFile,
  };
};

export function getFriendlyChatName(convo: ChatRoom): string {
  const friendlyTitle = convo.title
    ? convo.title.replace(/\s/g, '').length !== 0
      ? convo.title
      : `새 채팅`
    : `새 채팅`;

  // Truncate the title if it is too long
  return friendlyTitle.length > 60 ? friendlyTitle.substring(0, 60) + '...' : friendlyTitle;
}

export function getFriendlyChatNameByTitle(title: string): string {
  const friendlyTitle = title ? title : `새 채팅`;

  // Truncate the title if it is too long
  return friendlyTitle.length > 60 ? friendlyTitle.substring(0, 60) + '...' : friendlyTitle;
}
